import React from 'react';
import {
    FormGroup, Row, Col,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { isEqual } from 'lodash-es';
import { faWarehouse, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { clone } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import {
    AppPageForm,
    FlexCenterRow,
    /* FlexEndRow, */ FormDivider,
    onFieldChange,
    FormCheckbox, ToastMessage,
    Input,
    NumericInput,
} from '../common/forms/FormElements';
import { BaseFormViewModel } from '../common/ViewModel';
import CommonContext, { ApiRoutes, AppNavPaths, ServiceResponseResult } from '../Common';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { SaveButton } from '../common/forms/SaveButton';
import { PaySchedule } from './PaySchedule';

class PayScheduleForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();

        const stateBase = {
            paySchedule: new PaySchedule(),
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
        this.onSubmit = this.onSubmit.bind(this);
        this.onChange = this.onChange.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps
            && this.props.match.params.id !== (prevProps.match.params ?? {}).id
        ) {
            this.populateState();
        }
    }

    onChange = onFieldChange;

    onClearErrors = () => this.setState({ errors: {} });

    onCountiesChanged = (selection) => {
        const { paySchedule } = this.state;

        paySchedule.countyIds = selection?.map((x) => x.id);

        paySchedule.counties = '';
        if (paySchedule.countyIds != null && paySchedule.countyIds.length > 0) paySchedule.counties = paySchedule.countyIds.join(',');

        this.setState({
            paySchedule,
        });
    };

    onSubmit = async () => {
        // Clear any fluent api errors
        this.onClearErrors();
        this.setSaving(true);

        if (this.props.location.pathname == AppNavPaths.PayScheduleNew) {
            await this.create();
        } else {
            await this.update();
        }
    };

    onUSStateChanged = (selection) => {
        const { paySchedule } = this.state;

        paySchedule.stateId = selection?.id;
        paySchedule.countyIds = [];
        paySchedule.counties = '';

        this.setState({
            paySchedule,
        });
    };

    setSaving = (b) => this.setState((prevState) => ({ saving: b, originalData: clone(prevState.paySchedule) }));

    create = async () => {
        const { paySchedule } = this.state;
        paySchedule.id = 0;

        const resp = await httpClient.put(
            ApiRoutes.paySchedule.update(0),
            paySchedule,
        ).catch(this.handleSaveError);

        if (resp) {
            this.onPaySchedulesaved(resp);
        }
        this.setSaving(false);
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    async populateState() {
        const payScheduleId = this.props.match.params.id;

        const paySchedule = payScheduleId
            ? await httpClient.js(ApiRoutes.paySchedule.byId(payScheduleId))
            : new PaySchedule();

        if (paySchedule.countyIds != null) paySchedule.counties = paySchedule.countyIds.join(',');

        const originalData = clone(paySchedule);
        const countyId = 1; // PaySchedule is only for USA.
        const [usStates, usCounties] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.USStatesbyCountryId(countyId)),
            httpClient.js(ApiRoutes.USCounties.all()),
        ]);

        this.setState({
            originalData,
            paySchedule,
            loading: false,
            saving: false,
            usStates,
            usCounties,
        });
    }

    resetForm = () => this.setState({ formValidated: false });

    update = async () => {
        const { paySchedule } = this.state;

        const resp = await httpClient.put(
            ApiRoutes.paySchedule.update(paySchedule.id),
            paySchedule,
        ).catch(this.handleSaveError);

        if (resp) {
            this.onPaySchedulesaved(resp);
        }
        this.setSaving(false);
    };
    onPaySchedulesaved = (response) => {
        if (response.result === ServiceResponseResult.Ok) {
            toast.success(
                <ToastMessage
                    icon={faCheckCircle}
                    header="Save Successful"
                    message="Saved."
                />,
            );
            this.context.history.push(`${AppNavPaths.PaySchedule}`);
        } else {
            toast.error(
                <ToastMessage
                    icon={faExclamationTriangle}
                    header="There was a problem saving"
                    message={`${response.message}`}
                />,
            );
        }
    };
    render() {
        const {
            paySchedule,
            usStates,
            usCounties,
            errors,
            formValidated,
            saving,
            loading,
            originalData,
        } = this.state;

        const existing = !!paySchedule.id;
        const showConfirmationPrompt = (!isEqual(paySchedule, originalData));

        return (
            <>
                <FormPrompt
                    when={!saving && !!showConfirmationPrompt}
                    message="You have unsaved changes, are you sure you want to leave?"
                />

                <AppPageForm
                    backUrl={AppNavPaths.PaySchedule}
                    backLabel="Back to Pay Schedules"
                    formShown={this.context.formIsOpen}
                    formId="groupForm"
                    formHeadingIcon={faWarehouse}
                    formHeading={
                        !paySchedule.id
                            ? 'New Pay Schedule'
                            : 'Edit Pay Schedule'
                    }
                    formName="groupForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    saving={saving}
                    errors={errors}
                    onClearErrors={this.onClearErrors}
                    loading={loading}
                >
                    <Row className="pt-3">
                        <Col
                            xl="6"
                            lg="8"
                            md="10"
                            sm="12"
                            className="ml-auto mr-auto"
                        >
                            <Input
                                label="Name"
                                id="governingBody"
                                name="paySchedule.governingBody"
                                value={paySchedule.governingBody ?? ''}
                                onChange={this.onChange}
                                placeholder="Enter Name (max 150 characters)"
                                maxLength="150"
                                pattern="[^()/><\][\\\x22,;|]+"
                                required
                                error="Description is required and can only contain the following special characters: hyphens and periods."
                            />

                            <ValidatedSelect
                                id="paySchedule.stateId"
                                name="paySchedule.stateId"
                                label="State"
                                required
                                options={usStates}
                                value={
                                    (usStates ?? []).find(
                                        (s) => s.id === paySchedule.stateId,
                                    ) ?? ''
                                }
                                onChange={this.onUSStateChanged}
                                error="A state selection is required."
                            />

                            <ValidatedSelect
                                id="counties"
                                name="counties"
                                label="Counties"
                                isMulti
                                getOptionLabel={(option) => option.name}
                                getOptionValue={(option) => option.id}
                                options={(usCounties ?? []).filter(
                                    (c) => c.usStateId == paySchedule.stateId,
                                )}
                                value={
                                    (usCounties ?? []).filter((x) => (
                                        paySchedule.countyIds ?? []
                                    ).includes(x.id)) ?? ''
                                }
                                onChange={this.onCountiesChanged}
                            />

                            <Row>
                                <Col>
                                    <NumericInput
                                        label="Reg"
                                        id="reg"
                                        name="paySchedule.reg"
                                        value={paySchedule.reg ?? 0}
                                        onChange={this.onChange}
                                        step={0.01}
                                        required
                                    />
                                </Col>
                                <Col>
                                    <NumericInput
                                        label="OT"
                                        id="ot"
                                        name="paySchedule.ot"
                                        value={paySchedule.ot ?? 0}
                                        onChange={this.onChange}
                                        step={0.01}
                                        required
                                    />
                                </Col>
                                <Col>
                                    <NumericInput
                                        label="Fringe"
                                        id="fringe"
                                        name="paySchedule.fringe"
                                        value={paySchedule.fringe ?? 0}
                                        onChange={this.onChange}
                                        step={0.01}
                                        required
                                    />
                                </Col>
                            </Row>

                            <Row>
                                <Col>
                                    <FormGroup>
                                        <FormCheckbox
                                            small
                                            id="useDailyOT"
                                            checked={paySchedule.useDailyOT}
                                            onChange={(e) => {
                                                const { paySchedule } = this.state;
                                                paySchedule.useDailyOT = e.target.checked;
                                                this.setState({
                                                    paySchedule,
                                                });
                                            }}
                                            labelText="Daily Overtime"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col>
                                    {paySchedule.useDailyOT && (
                                        <NumericInput
                                            label="Daily Overtime Threshold"
                                            id="dailyOTThreshold"
                                            name="paySchedule.dailyOTThreshold"
                                            value={
                                                paySchedule.dailyOTThreshold
                                                ?? ''
                                            }
                                            onChange={this.onChange}
                                            placeholder="Hours"
                                            step={0.01}
                                            required
                                        />
                                    )}
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    <FormDivider />

                    <FlexCenterRow className="mb-3">
                        <SaveButton
                            saving={!!this.state.saving}
                            label={!existing ? 'Save New Pay Schedule' : 'Save'}
                        />
                    </FlexCenterRow>
                </AppPageForm>
            </>
        );
    }
}
export default PayScheduleForm;
